import { packingTemplateIdFormat } from "@redotech/redo-fulfillment-id";
import { WeightSchema } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { PackingTemplateSchema } from "@redotech/redo-inventory-common/packing-template";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import {
  productFamilyIdFormat,
  productIdFormat,
} from "@redotech/redo-product-id";
import * as z from "zod";

const ProductSearchResultSchema_ = z.object({
  id: productIdFormat.schema,
  title: z.string(),
  sku: z.string().nullable(),
  upc: z.string().nullable(),
  barcodes: z.array(z.string()),
  imageUrl: z.string().nullable(),
  currentQuantity: z.bigint().default(0n), // On hand at the location
  // Null when the caller did not scope the search to one location.
  committedQuantity: z.bigint().nullable().default(null),
  reservedQuantity: z.bigint().nullable().default(null),
  availableQuantity: z.bigint().nullable().default(null),
  accountId: z.string(),
  accountName: z.string(),
  weight: WeightSchema.nullable().default(null),
  defaultPackingTemplateId: packingTemplateIdFormat.schema
    .nullable()
    .default(null),
  packingTemplates: z.array(PackingTemplateSchema).default([]),
  productFamilyId: productFamilyIdFormat.schema.nullable().default(null),
  productFamilyTitle: z.string().nullable().default(null),
  optionsSortKey: z.array(z.number()).default([]),
  /** A lot-tracked product's stock is counted and moved by lot. */
  trackLotFulfillment: z.boolean().nullish(),
});

export const ProductSearchResultSchema: z.ZodType<ProductSearchResult> =
  ProductSearchResultSchema_;
export interface ProductSearchResult extends z.infer<
  typeof ProductSearchResultSchema_
> {}

export const searchProducts = {
  name: "searchProducts" as const,
  input: z.object({
    query: z.string(),
    locationId: locationIdFormat.schema.nullish(),
    limit: z.number().min(1).max(50).default(20),
    /** When true and locationId is provided, only return products with stock > 0 at that location */
    filterByStock: z.boolean().optional(),
    /** Omit to preserve the caller's existing search scope. */
    includeArchivedProducts: z.boolean().optional(),
    /** Version 2 applies the archived filter independently of the retired flag. */
    archivedFilterVersion: z.literal(2).optional(),
  }),
  output: z.object({ products: z.array(ProductSearchResultSchema) }),
};
